import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  getIncomingSms,
  markIncomingSmsRead,
  notifyIncomingSmsUnread,
  hasUnreadIncomingSms
} from '../api';
import {
  Paper, Box, Button, Chip, Typography, Snackbar, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, TablePagination
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';

const READ_FILTERS = ['all','unread','read'];

export default function IncomingSms() {
  const theme = useTheme();
  const [endDate, setEndDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(dayjs().subtract(13,'day'));
  const [readFilter, setReadFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRpp] = useState(50);

  const params = useMemo(() => ({
    read: readFilter,
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    limit: rowsPerPage,
    offset: page * rowsPerPage
  }), [readFilter, startDate, endDate, page, rowsPerPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getIncomingSms(params);
      setRows(res?.data || []);
      setMeta(res?.meta || null);
      
      // Notify navigation about unread status using the global check
      const hasUnread = await hasUnreadIncomingSms();
      notifyIncomingSmsUnread(hasUnread);
    } catch (e) {
      setToast({ severity: 'error', msg: 'Failed to load incoming SMS' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { fetchData(); }, [readFilter, startDate, endDate, page, rowsPerPage]);

  const total = meta?.total ?? rows.length;

  const onMarkRead = async (id) => {
    try {
      await markIncomingSmsRead(id);
      const updatedRows = rows.map(r => (r.id === id ? { ...r, admin_read: 1 } : r));
      setRows(updatedRows);
      setToast({ severity: 'success', msg: 'Marked as read' });
      
      // Notify navigation about updated unread status using the global check
      const hasUnread = await hasUnreadIncomingSms();
      notifyIncomingSmsUnread(hasUnread);
    } catch (e) {
      setToast({ severity: 'error', msg: e?.response?.data?.message || 'Could not mark as read' });
    }
  };

  const columns = ['Username', 'Phone', 'Created at', 'Message', 'Response', 'Actions'];

  return (
    <>
      <PageHeader title="Incoming SMS" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>Filter:</Typography>
          {READ_FILTERS.map(f => (
            <Button
              key={f}
              size="small"
              variant={readFilter === f ? 'contained' : 'outlined'}
              onClick={() => { setPage(0); setReadFilter(f); }}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
          <Box sx={{ flexGrow: 1 }} />
          <DatePicker label="Start" value={startDate} onChange={(d)=>d && setStartDate(d)} slotProps={{ textField:{ size:'small' } }} />
          <DatePicker label="End"   value={endDate}   onChange={(d)=>d && setEndDate(d)}     slotProps={{ textField:{ size:'small' } }} />
          <Button variant="outlined" onClick={()=>{ setPage(0); fetchData(); }}>Apply</Button>
          {meta && (
            <Chip variant="outlined" label={`Window: ${meta.start?.slice(0,10)} → ${meta.end?.slice(0,10)} • ${total} found`} />
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', py:8 }}><CircularProgress /></Box>
        ) : !rows.length ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign:'center', py:6 }}>
            No messages match your filters.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => <TableCell key={c} sx={{ fontWeight:'bold' }}>{c}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const readableDate = r.created_at ? new Date(r.created_at).toLocaleString() : '';
                  const isRead = Number(r.admin_read) === 1;
                  return (
                    <TableRow key={r.id} sx={{ '&:nth-of-type(odd)': { backgroundColor:'action.hover' } }}>
                      <TableCell>
                        {r.user_id ? (
                          <Link
                            to={`/user/${r.user_id}`}
                            state={{ from: { label: 'Incoming SMS', path: '/incoming-sms' } }}
                            style={{ textDecoration:'none', color: theme.palette.primary.main, fontWeight:600 }}
                          >
                            {r.username || `User #${r.user_id}`}
                          </Link>
                        ) : (
                          'null'
                        )}
                      </TableCell>
                      <TableCell>{r.phone_number || ''}</TableCell>
                      <TableCell>{readableDate}</TableCell>
                      <TableCell style={{ whiteSpace:'pre-wrap' }}>{r.message_body || ''}</TableCell>
                      <TableCell style={{ whiteSpace:'pre-wrap' }}>{r.response_message || ''}</TableCell>
                      <TableCell align="right">
                        {isRead ? (
                          <Chip size="small" label="Read" color="success" variant="outlined" />
                        ) : (
                          <Button size="small" variant="contained" onClick={() => onMarkRead(r.id)}>
                            Read
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 250]}
            />
          </>
        )}
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical:'bottom', horizontal:'right' }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : null}
      </Snackbar>
    </>
  );
}


